import { Injectable } from '@angular/core';
import { IRenderData, IStorageMethod, IStorageOpts, ITransferItemDownload } from 'src/sync';
import { ERROR_CODES, ErrCode } from '../../shared/models';
import { LoggerService } from '../logger.service';
import { StreamingSaveSession } from './streaming-save-session';
import { StreamingSaveService } from './streaming-save.service';

/**
 * Writes decrypted chunks straight to a user-chosen file via the File System Access API, so a download
 * is bounded by free disk space rather than the origin-storage quota.
 */
@Injectable({
  providedIn: 'root',
})
export class StorageStreamingService implements IStorageMethod {
  public readonly isStreamingToDisk = true;

  /** Zero means "no maximum", which is what exempts this store from the 3020 size gate. */
  public maxsize = 0;

  private sessions: { [key: string]: StreamingSaveSession } = {};

  constructor(
    private streamingSaveService: StreamingSaveService,
    private log: LoggerService,
  ) {}

  public async init(initOpts: IStorageOpts): Promise<number> {
    if (!initOpts.streamingHandle) {
      throw new ErrCode(ERROR_CODES.BROWSER_UNSUPPORTED_DOWNLOAD);
    }
    const key = this.keyOf(initOpts);
    this.sessions[key] = await this.streamingSaveService.openSession(initOpts.streamingHandle);
    return 1;
  }

  public async writeChunk(
    tItem: ITransferItemDownload,
    bytearray: number[] | Uint8Array[] | ArrayLike<number>,
    offset: number,
  ): Promise<number> {
    const key = this.keyOf(tItem);
    const session = this.sessions[key];
    if (!session) {
      throw new ErrCode(ERROR_CODES.BROWSER_FILE_WRITE_FAILED);
    }
    if (offset !== session.bytesWritten) {
      this.log.error(`[Streaming save] chunk at ${offset} does not follow ${session.bytesWritten} bytes written`);
      throw new ErrCode(ERROR_CODES.BROWSER_FILE_WRITE_FAILED);
    }
    await session.write(this.toWritable(bytearray));
    return 1;
  }

  /** Commits the destination. Rejects without committing if the decrypted output is the wrong length. */
  public async finalize(tItem: ITransferItemDownload): Promise<void> {
    const key = this.keyOf(tItem);
    const session = this.sessions[key];
    if (!session) {
      throw new ErrCode(ERROR_CODES.BROWSER_FILE_WRITE_FAILED);
    }
    try {
      if (session.bytesWritten !== tItem.filesize) {
        this.log.error(`[Streaming save] wrote ${session.bytesWritten} bytes, expected ${tItem.filesize}`);
        await session.abort();
        throw new ErrCode(ERROR_CODES.BROWSER_FILE_WRITE_FAILED);
      }
      await session.close();
    } finally {
      this.clear(key);
    }
  }

  public async cancel(tItem: ITransferItemDownload, reason?: unknown): Promise<void> {
    const key = this.keyOf(tItem);
    const session = this.sessions[key];
    if (!session) {
      return;
    }
    this.clear(key);
    await session.abort(reason);
  }

  public exists(): Promise<boolean> {
    return Promise.resolve(false);
  }

  public clearSpace(): Promise<void> {
    return Promise.resolve();
  }

  /** Never called on this store — completion is `close()`, not a render handoff. Loud rather than silent. */
  public getRenderData(): Promise<IRenderData> {
    return Promise.reject(new ErrCode(ERROR_CODES.BROWSER_UNSUPPORTED_DOWNLOAD));
  }

  private keyOf(source: IStorageOpts | ITransferItemDownload): string {
    return `${source.blobtype}-${source.cachekey}`;
  }

  /**
   * Three shapes reach here: a bare Uint8Array (worker, sub-packet chunk), an array of per-packet
   * Uint8Arrays (worker, every full 25 MB chunk), and a flat number[] (legacy engine).
   */
  private toWritable(bytearray: number[] | Uint8Array[] | ArrayLike<number>): Uint8Array | Blob {
    if (bytearray instanceof Uint8Array) {
      return bytearray;
    }
    if (Array.isArray(bytearray) && bytearray.length > 0 && ArrayBuffer.isView(bytearray[0])) {
      return new Blob(bytearray as Uint8Array[]);
    }
    return new Uint8Array(bytearray as ArrayLike<number>);
  }

  private clear(key: string): void {
    delete this.sessions[key];
  }
}
